import { Box, Typography, Card, Stack, Button, Chip, CircularProgress, Alert, Input, IconButton, Tooltip, Switch, FormControl, FormLabel } from "@mui/joy";
import { SettingsRounded, AddRounded, EditRounded, DeleteRounded, SearchRounded, WarningRounded, ToggleOnRounded, ToggleOffRounded } from '@mui/icons-material';
import { useContext, useEffect, useState } from "react";
import { LayoutContext } from "../context";
import { APIGetDangerKeywords, APICreateDangerKeyword, APIUpdateDangerKeyword, APIDeleteDangerKeyword, APIToggleDangerKeywordActive } from "../api";
import ModalDangerKeyword from "../components/modals/ModalDangerKeyword";
import PushNotificationSettings from "../components/PushNotificationSettings";
import CalendarSettings from '../components/calendar/CalendarSettings';


export default function SettingsPage() {
    const { currentDoctor, token } = useContext(LayoutContext);
    const [keywords, setKeywords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Состояния для модальных окон
    const [modalState, setModalState] = useState({
        open: false,
        mode: 'create', // 'create' | 'edit' | 'delete'
        keyword: null
    });
    
    const [actionLoading, setActionLoading] = useState({
        create: false,
        edit: false,
        delete: false,
        toggle: {}
    });

    // Проверяем права доступа
    const hasAccess = currentDoctor?.type_profile?.includes('moderator') || 
                  currentDoctor?.type_profile?.includes('admin') || 
                  currentDoctor?.type_profile?.includes('supervisor');

    // Загрузка ключевых слов
    const loadKeywords = async () => {
        if (!hasAccess) return;
        
        try {
            setLoading(true);
            setError(null);
            
            const response = await APIGetDangerKeywords(token);
            
            if (response.success) {
                setKeywords(response.keywords || []);
            } else {
                setError(response.error || 'Ошибка при загрузке ключевых слов');
            }
        } catch (error) {
            console.error("Ошибка при загрузке ключевых слов:", error);
            setError("Ошибка при загрузке ключевых слов");
        } finally {
            setLoading(false);
        }
    };

    // Открытие модального окна
    const openModal = (mode, keyword = null) => {
        setModalState({
            open: true,
            mode,
            keyword
        });
    };

    // Закрытие модального окна
    const closeModal = () => {
        setModalState({
            open: false,
            mode: 'create',
            keyword: null
        });
    };

    // Создание ключевого слова
    const handleCreateKeyword = async (keywordText) => {
        try {
            setActionLoading(prev => ({ ...prev, create: true }));
            setError(null);

            const response = await APICreateDangerKeyword(token, {
                keyword: keywordText
            });

            if (response.success) {
                setSuccess('Ключевое слово успешно создано');
                closeModal();
                await loadKeywords();
            } else {
                setError(response.error || 'Ошибка при создании ключевого слова');
            }
        } catch (error) {
            console.error("Ошибка при создании ключевого слова:", error);
            setError("Ошибка при создании ключевого слова");
        } finally {
            setActionLoading(prev => ({ ...prev, create: false }));
        }
    };

    // Редактирование ключевого слова
    const handleEditKeyword = async (keywordText) => {
        try {
            setActionLoading(prev => ({ ...prev, edit: true }));
            setError(null);

            const response = await APIUpdateDangerKeyword(token, modalState.keyword.id, {
                keyword: keywordText
            });

            if (response.success) {
                setSuccess('Ключевое слово успешно обновлено');
                closeModal();
                await loadKeywords();
            } else {
                setError(response.error || 'Ошибка при обновлении ключевого слова');
            }
        } catch (error) {
            console.error("Ошибка при обновлении ключевого слова:", error);
            setError("Ошибка при обновлении ключевого слова");
        } finally {
            setActionLoading(prev => ({ ...prev, edit: false }));
        }
    };

    // Удаление ключевого слова
    const handleDeleteKeyword = async (keywordId) => {
        try {
            setActionLoading(prev => ({ ...prev, delete: true }));
            setError(null);

            const response = await APIDeleteDangerKeyword(token, keywordId);

            if (response.success) {
                setSuccess('Ключевое слово успешно удалено');
                closeModal();
                await loadKeywords();
            } else {
                setError(response.error || 'Ошибка при удалении ключевого слова');
            }
        } catch (error) {
            console.error("Ошибка при удалении ключевого слова:", error);
            setError("Ошибка при удалении ключевого слова");
        } finally {
            setActionLoading(prev => ({ ...prev, delete: false }));
        }
    };

    // Переключение активности ключевого слова
    const handleToggleActive = async (keyword) => {
        try {
            setActionLoading(prev => ({ 
                ...prev, 
                toggle: { ...prev.toggle, [keyword.id]: true } 
            }));
            setError(null);

            const response = await APIToggleDangerKeywordActive(token, keyword.id);

            if (response.success) {
                setSuccess(`Ключевое слово ${response.keyword.is_active ? 'активировано' : 'деактивировано'}`);
                await loadKeywords();
            } else {
                setError(response.error || 'Ошибка при изменении статуса ключевого слова');
            }
        } catch (error) {
            console.error("Ошибка при изменении статуса ключевого слова:", error);
            setError("Ошибка при изменении статуса ключевого слова");
        } finally {
            setActionLoading(prev => ({ 
                ...prev, 
                toggle: { ...prev.toggle, [keyword.id]: false } 
            }));
        }
    };

    // Обработчик сохранения в модальном окне
    const handleModalSave = (keywordText) => {
        if (modalState.mode === 'create') {
            handleCreateKeyword(keywordText);
        } else if (modalState.mode === 'edit') {
            handleEditKeyword(keywordText);
        }
    };

    // Обработчик удаления в модальном окне
    const handleModalDelete = () => {
        if (modalState.keyword) {
            handleDeleteKeyword(modalState.keyword.id);
        }
    };

    // Фильтрация ключевых слов по поисковому запросу
    const filteredKeywords = keywords.filter(keyword =>
        keyword.keyword.toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        loadKeywords();
    }, []);

    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    if (!hasAccess) {
        return (
            <Box sx={{ display: 'flex' }}>
                <Box
                    component="main"
                    className="MainContent"
                    sx={{
                        px: { xs: 2, md: 6 },
                        pt: 2,
                        pb: { xs: 2, sm: 2, md: 3 },
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        gap: 1,
                    }}
                >
                    <Alert color="danger">
                        Доступ запрещен. Требуются права модератора или администратора.
                    </Alert>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex' }}>
            <Box
                component="main"
                className="MainContent"
                sx={{
                    px: { xs: 2, md: 6 },
                    pt: 2,
                    pb: { xs: 2, sm: 2, md: 3 },
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    gap: 1,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        mb: 1,
                        gap: 1,
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'start', sm: 'center' },
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography level="h2" component="h1">
                        {/* <SettingsRounded sx={{ mr: 1 }} /> */}
                        Настройки
                    </Typography>
                </Box>

                {error && (
                    <Alert color="danger" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert color="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                <PushNotificationSettings />
                <CalendarSettings />

                {/* Секция ключевых слов опасных сообщений */}
                <Card variant="outlined" sx={{ p: 3 }}>
                    <Stack direction={{ xs: 'column', sm: "row" }} spacing={{ xs: 2, sm: 0 }} justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                        <Box>
                            <Typography level="h3" fontSize="lg" sx={{ mb: 0.5 }}>
                                Ключевые слова опасных сообщений
                            </Typography>
                            <Typography level="body2" color="neutral">
                                Система автоматически помечает сообщения как опасные при обнаружении этих слов
                            </Typography>
                        </Box>
                        <Button
                            startDecorator={<AddRounded />}
                            onClick={() => openModal('create')}
                            sx={{ minWidth: 190, width: { xs: '100%', sm: 'auto' } }}
                        >
                            Добавить слово
                        </Button>
                    </Stack>

                    {/* Поиск */}
                    <Input
                        size="sm"
                        placeholder="Поиск ключевых слов..."
                        startDecorator={<SearchRounded />}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ mb: 2 }}
                    />

                    {/* Список ключевых слов */}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : filteredKeywords.length > 0 ? (
                        <Stack spacing={1}>
                            {filteredKeywords.map((keyword) => (
                                <Card key={keyword.id} variant="outlined" sx={{ p: 2 }}>
                                    <Stack 
                                        direction={{ xs: 'column', sm: 'row' }} 
                                        spacing={2} 
                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        justifyContent="space-between"
                                    >
                                        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                                            <WarningRounded 
                                                color={keyword.is_active ? "warning" : "disabled"} 
                                            />
                                            <Box>
                                                <Typography level="title-md" sx={{ fontWeight: 700 }}>
                                                    {keyword.keyword}
                                                </Typography>
                                                <Typography level="title-sm" color="neutral">
                                                    Добавлено: {new Date(keyword.date_create).toLocaleDateString('ru-RU')}
                                                </Typography>
                                            </Box>
                                        </Stack>

                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Tooltip title={keyword.is_active ? "Деактивировать" : "Активировать"}>
                                                <IconButton
                                                    variant="soft"
                                                    color={keyword.is_active ? "success" : "neutral"}
                                                    size="sm"
                                                    loading={actionLoading.toggle[keyword.id]}
                                                    onClick={() => handleToggleActive(keyword)}
                                                >
                                                    {keyword.is_active ? <ToggleOnRounded /> : <ToggleOffRounded />}
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Редактировать">
                                                <IconButton
                                                    variant="soft"
                                                    color="primary"
                                                    size="sm"
                                                    onClick={() => openModal('edit', keyword)}
                                                >
                                                    <EditRounded />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Удалить">
                                                <IconButton
                                                    variant="soft"
                                                    color="danger"
                                                    size="sm"
                                                    onClick={() => openModal('delete', keyword)}
                                                >
                                                    <DeleteRounded />
                                                </IconButton>
                                            </Tooltip>

                                            <Chip 
                                                variant="soft" 
                                                color={keyword.is_active ? "success" : "neutral"} 
                                                size="sm"
                                            >
                                                {keyword.is_active ? 'Активно' : 'Неактивно'}
                                            </Chip>
                                        </Stack>
                                    </Stack>
                                </Card>
                            ))}
                        </Stack>
                    ) : (
                        <Alert color="neutral" sx={{ mt: 2 }}>
                            {searchTerm ? 'Ключевые слова не найдены' : 'Ключевые слова не добавлены'}
                        </Alert>
                    )}
                </Card>

                {/* Модальное окно для управления ключевыми словами */}
                <ModalDangerKeyword
                    open={modalState.open}
                    onClose={closeModal}
                    mode={modalState.mode}
                    keyword={modalState.keyword}
                    onSave={handleModalSave}
                    onDelete={handleModalDelete}
                    loading={
                        actionLoading.create || 
                        actionLoading.edit || 
                        actionLoading.delete
                    }
                />
            </Box>
        </Box>
    );
}